import type { Job } from "../../shared/types.ts";

export const FAMILIES: Record<string, string> = {
  swe: "Software",
  ml_ai: "ML and AI",
  data: "Data",
  pm: "Product",
  design: "Design",
  security: "Security",
  it: "IT",
  hardware: "Hardware",
  civil_industrial: "Civil and industrial",
  research: "Research",
  ops: "Operations",
  sales: "Sales",
  marketing: "Marketing",
  customer_success: "Customer success",
  finance: "Finance",
  consulting: "Consulting",
  hr: "People",
  legal: "Legal",
  healthcare: "Healthcare",
};

export const SENIORITY: Record<string, string> = {
  intern: "Intern",
  new_grad: "New grad",
  entry: "Entry level",
  mid: "Mid level",
  senior: "Senior",
  staff_plus: "Staff and up",
  manager: "Manager",
};

export const WORKPLACE: Record<string, string> = { remote: "Remote", hybrid: "Hybrid", onsite: "On-site" };

const VISA: Record<string, { tone: "ok" | "bad"; text: string }> = {
  sponsors: { tone: "ok", text: "Sponsors visas" },
  no_sponsorship: { tone: "bad", text: "No visa sponsorship" },
  citizens_only: { tone: "bad", text: "Citizens only" },
};

/** The visa stance worth a line, or null for "unknown" and missing. */
export const visaNote = (visa: string | null | undefined) => (visa && Object.hasOwn(VISA, visa) ? VISA[visa]! : null);

function symbol(currency: string | null): string {
  if (!currency) return "$";
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency, currencyDisplay: "narrowSymbol" }).formatToParts(0).find((p) => p.type === "currency")
        ?.value ?? `${currency} `
    );
  } catch {
    return `${currency} `;
  }
}

const short = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n)));

/** "$120k–150k", "$45/h", or null when the posting lists no pay. */
export function pay(j: Pick<Job, "salaryMin" | "salaryMax" | "salaryCurrency" | "salaryPeriod">): string | null {
  const lo = j.salaryMin ?? j.salaryMax;
  const hi = j.salaryMax ?? j.salaryMin;
  if (lo == null || hi == null) return null;
  const s = symbol(j.salaryCurrency);
  const unit = j.salaryPeriod === "hour" ? "/h" : j.salaryPeriod === "month" ? "/mo" : "";
  return lo === hi ? `${s}${short(lo)}${unit}` : `${s}${short(lo)}–${short(hi)}${unit}`;
}

/** A location that only says how the job is worked ("Remote-Friendly (Travel-Required)", "Hybrid"), not where. */
const MODE_ONLY = /^(remote|hybrid|on-?site|anywhere|work from home|wfh|flexible)\b[\s\w-]*$/i;

/** Real places, with country suffixes for the US and parentheticals dropped. */
export function places(j: Pick<Job, "locations">): string[] {
  return j.locations
    .map((l) => l.replace(/\s*\([^)]*\)/g, "").replace(/, (United States( of America)?|USA|US)$/i, "").trim())
    .filter((l) => l && !MODE_ONLY.test(l));
}

/** First real place and "+N" for the rest: "San Francisco, CA +2". Falls back to the workplace. */
export function place(j: Pick<Job, "locations" | "workplace">): string {
  const all = places(j);
  const first = all[0];
  if (!first) return WORKPLACE[j.workplace] ?? (j.locations.some((l) => /remote/i.test(l)) ? "Remote" : "Location not listed");
  return all.length > 1 ? `${first} +${all.length - 1}` : first;
}

/** ISO or SQLite `datetime('now')` text (UTC, no zone) to epoch ms. */
export const utc = (s: string) => Date.parse(s.includes("T") ? s : `${s.replace(" ", "T")}Z`);

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const ms = now - utc(iso);
  if (!Number.isFinite(ms)) return "";
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 14) return d === 1 ? "yesterday" : `${d} days ago`;
  const w = Math.round(d / 7);
  if (w < 9) return `${w} weeks ago`;
  return `${Math.round(d / 30)} months ago`;
}

/** "Posted today", "Posted 1 day ago", "Posted 3 days ago", the way job alert emails write it. Empty without a date. */
export function posted(iso: string | null | undefined, now = Date.now()): string {
  const ms = iso ? now - utc(iso) : NaN;
  if (!Number.isFinite(ms)) return "";
  const d = Math.max(0, Math.floor(ms / 86_400_000));
  return d ? `Posted ${d} ${d === 1 ? "day" : "days"} ago` : "Posted today";
}

/** A count with thousands separators: "1,200". */
export const num = (n: number) => n.toLocaleString("en-US");

/** A UTC date as "Nov 3". */
export const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function list(items: string[], max = 3): string {
  return items.length > max ? `${items.slice(0, max).join(", ")} +${items.length - max}` : items.join(", ");
}

/** "Good morning, Ada." by the hour of the day; no name drops to "Good morning." */
export function greeting(firstName: string | null | undefined, hour = new Date().getHours()): string {
  const part = hour < 5 ? "evening" : hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const name = firstName?.trim();
  return name ? `Good ${part}, ${name}.` : `Good ${part}.`;
}

export type Block = { kind: "h" | "p"; text: string } | { kind: "ul"; items: string[] };

const BULLET = /^\s*(?:[-•*·▪●◦]|\d{1,2}[.)])\s+/;

/** Description text as headings, paragraphs and lists: a short line with no closing punctuation is a heading. */
export function blocks(text: string, title?: string): Block[] {
  const out: Block[] = [];
  const lines = text.split("\n").map((l) => l.trim());
  lines.forEach((line, i) => {
    if (!line) return;
    if (BULLET.test(line)) {
      const item = line.replace(BULLET, "");
      const last = out.at(-1);
      if (last?.kind === "ul") last.items.push(item);
      else out.push({ kind: "ul", items: [item] });
      return;
    }
    const more = lines.slice(i + 1).some(Boolean);
    const heading = more && line.length <= 60 && !/[.!?,;]$/.test(line) && !/^https?:/.test(line);
    out.push(heading ? { kind: "h", text: line.replace(/:$/, "") } : { kind: "p", text: line });
  });
  const first = out[0];
  const shown = title && first && first.kind !== "ul" && first.text.toLowerCase() === title.toLowerCase() ? out.slice(1) : out;
  return shown.slice(0, 120);
}

